import {
	generatePath,
	Navigate,
	Outlet,
	useLocation,
	useOutletContext
} from 'react-router'
import { useGetPhotosetReviewState } from '@/features/host-review/queries/use-get-photoset-review-state'
import { useRedirectEditingPhotoset } from '@/features/photoset/hooks/use-redirect-editing-photoset'
import { useGetPhotoset } from '@/features/photoset/queries/use-get-photoset'
import type { PhotosetURLParams } from '@/features/photoset/types'
import { routes } from '@/routes'

export const PhotosetEditingRoute = () => {
	const location = useLocation()
	const outletContext = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const { data: photoset } = useGetPhotoset(
		{ id: outletContext.urlParams.id },
		{ staleTime: Number.POSITIVE_INFINITY }
	)

	// A host review does not move the photoset step - a closed review still reads
	// `retouching_revision_approved` - so the step redirect would bounce the
	// operator straight off the review surface. The review state decides the
	// screen for these photosets, and it has to be known before the redirect can
	// safely run, hence suspending while it loads too.
	const {
		data: reviewState,
		isPending: isPendingReviewState,
		fetchStatus: reviewStateFetchStatus
	} = useGetPhotosetReviewState(photoset?.id)
	const hasReview =
		reviewState?.reviewState === 'pending_host_review' ||
		reviewState?.reviewState === 'review_closed'

	// Only an in-flight request holds the redirect. Keyed on `fetchStatus`
	// rather than `isError` because the query client runs in `networkMode:
	// 'online'`: offline, the query is *paused* - forever pending, never an
	// error - and suspending on that would strand every photoset, reviewed or
	// not, on whatever URL it was opened at. A failed or paused read must route
	// exactly as it did before this feature existed.
	const isWaitingForReviewState =
		isPendingReviewState && reviewStateFetchStatus === 'fetching'

	// Redirect the user to the current QA Step
	useRedirectEditingPhotoset({
		isSuspended: isWaitingForReviewState || hasReview,
		photosetId: photoset?.id,
		stepId: photoset?.step_id
	})

	// With the step redirect suspended, a reviewed photoset still has to land
	// somewhere: the completed-steps route is where the waiting and folder views
	// live, and it is the only editing screen that reads the review.
	if (hasReview && photoset?.id) {
		const reviewPath = generatePath(
			routes.PHOTOSET_CURATION_COMPLETED.path,
			{
				id: String(photoset.id)
			}
		)

		if (location.pathname !== reviewPath) {
			return <Navigate replace to={reviewPath} />
		}
	}

	return <Outlet context={outletContext} />
}
